import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	HoldingEventRes,
	HoldingEventType,
	UpdateHoldingEventDto,
} from '@dealstack/shared/lib/generated';
import {
	CreateHoldingsEventDto,
	CreateHoldingsEventShape,
	UpdateHoldingsEventShape,
} from '@dealstack/shared/lib/common/zod/domain/holdings/holdings.zod.ts';
import { useMutation } from '@tanstack/react-query';
import {
	createHoldingsEvent,
	deleteHoldingsEvents,
	updateHoldingsEvent,
} from '@dealstack/frontend/app/services/holdingsEvent.service.ts';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { ComboboxItem } from '@mantine/core';
import {
	generateHoldingEventDescription,
	getPersonsLabelsWithIdsForSelect,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import { resolveEditFieldValue } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/utils.ts';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { cleanFileName } from '@dealstack/shared/lib/common/file/file';

export enum CREATE_HOLDING_EVENT_FORM_FIELD {
	id = 'id',
	date = 'date',
	type = 'type',
	note = 'note',
	pledgeToPersonId = 'pledgeToPersonId',
	pledgeToString = 'pledgeToString',
	pledgeFromHolderId = 'pledgeFromHolderId',
	pledgeFromSecurityId = 'pledgeFromSecurityId',
	pledgeFromString = 'pledgeFromString',
	assignedToPersonId = 'assignedToPersonId',
	assignedToString = 'assignedToString',
	description = 'description',
	documentUrl = 'documentUrl',
	documentName = 'documentName',
	fileId = 'fileId',
}

export const useHoldingEventDrawerController = (
	refreshHoldingsList: () => void,
	onClose: (securityEventId?: string) => void,
	holding?: RegularViewTableItemData,
	holdingsEvents?: HoldingEventRes[]
) => {
	const tCapManage = useTranslationCapManage();
	const [selectAll, setSelectAll] = useState(false);
	const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);

	const { dealId } = useContext(DealContext);
	const { persons } = useContext(CapManageControllerContext);

	const holdingsEvent =
		holdingsEvents && holdingsEvents.length > 0 ? holdingsEvents[0] : undefined;

	const createHoldingsEventRequest = useMutation({
		mutationFn: createHoldingsEvent,
	});

	const updateHoldingsEventRequest = useMutation({
		mutationFn: updateHoldingsEvent,
	});

	const deleteHoldingsEventRequest = useMutation({
		mutationFn: deleteHoldingsEvents,
	});

	const entitiesSelectOptions: ComboboxItem[] = useMemo(
		() => getPersonsLabelsWithIdsForSelect(persons),
		[persons]
	);

	const DEFAULT_FORM_VALUES = useMemo(() => {
		return {
			[CREATE_HOLDING_EVENT_FORM_FIELD.date]: null,
			[CREATE_HOLDING_EVENT_FORM_FIELD.type]: HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES,
			holderId: holding?.holderId || '',
			securityId: holding?.securityId || '',
			[CREATE_HOLDING_EVENT_FORM_FIELD.note]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromString]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToPersonId]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl]: undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.documentName]: undefined,
		} as unknown as CreateHoldingsEventDto;
	}, [holding?.holderId, holding?.securityId]);

	const createHoldingsEventForm = useForm<CreateHoldingsEventDto>({
		mode: 'all',
		resolver: zodResolver(CreateHoldingsEventShape),
		defaultValues: DEFAULT_FORM_VALUES,
		shouldFocusError: false,
	});

	const DEFAULT_EDIT_FORM_VALUES: UpdateHoldingEventDto = useMemo(() => {
		return {
			id: holdingsEvent?.id || '',
			[CREATE_HOLDING_EVENT_FORM_FIELD.date]: holdingsEvent?.date || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.type]: holdingsEvent?.type,
			[CREATE_HOLDING_EVENT_FORM_FIELD.note]: holdingsEvent?.note || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId]:
				holdingsEvent?.pledgeToPersonId ?? null,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString]:
				holdingsEvent?.pledgeToString || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId]:
				holdingsEvent?.pledgeFromHolderId ?? null,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId]:
				holdingsEvent?.pledgeFromSecurityId ?? null,
			[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromString]:
				holdingsEvent?.pledgeFromString || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString]:
				holdingsEvent?.assignedToString || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToPersonId]:
				holdingsEvent?.assignedToPersonId ?? null,
			[CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl]: holdingsEvent?.documentUrl || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.documentName]:
				holdingsEvent?.documentName || undefined,
			[CREATE_HOLDING_EVENT_FORM_FIELD.fileId]: holdingsEvent?.fileId || undefined,
		};
	}, [holding?.holderId, holding?.securityId, holdingsEvent]);

	const editHoldingsEventForm = useForm<UpdateHoldingEventDto>({
		mode: 'all',
		resolver: zodResolver(UpdateHoldingsEventShape),
		defaultValues: DEFAULT_EDIT_FORM_VALUES,
		shouldFocusError: false,
	});

	const uploadDocument = async (
		eventId: string
	): Promise<{ documentUrl: string; documentName: string; documentMd5: string }> => {
		if (!uploadedFile || !dealId) {
			throw new Error('No file to upload or missing deal ID.');
		}

		try {
			const entityMap = {
				HoldingEvent: eventId,
			};

			const fileData = [
				{
					fileName: cleanFileName(uploadedFile.name),
					mimeType: uploadedFile.type,
					extension: DocumentService.getFileExtensionFromName(uploadedFile.name),
				},
			];

			const preSignedUrl = await DocumentService.createPreSignedUrls({
				fileData,
				entityMap,
				dealId,
			});

			if (!preSignedUrl || !(preSignedUrl.length > 0)) {
				throw new Error('Failed to get pre-signed URL');
			}

			const fileInfo = preSignedUrl[0];

			const md5 = await DocumentService.putUploadToPreSignedUrl({
				preSignedUrl: fileInfo.uploadPreSignedUrl,
				file: uploadedFile,
			});

			return {
				documentUrl: fileInfo.fileKey,
				documentName: fileInfo.fileName,
				documentMd5: md5,
			};
		} catch (error) {
			showToastV2({
				title: tCapManage('holdings.event.document_failed'),
				state: 'error',
			});
			throw error;
		}
	};

	const onSubmit = () => (formValues: CreateHoldingsEventDto) => {
		if (!holding?.holderId || !holding?.securityId) {
			return;
		}

		const payload: CreateHoldingsEventDto = {
			date: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.date],
			holderId: holding.holderId,
			securityId: holding.securityId,
			type: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.type],
			note: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.note],
			pledgeToPersonId: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId],
			pledgeToString: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString],
			pledgeFromHolderId: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId],
			pledgeFromSecurityId: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId],
			pledgeFromString: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromString],
			assignedToPersonId: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToPersonId],
			assignedToString: formValues[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString],
		};

		try {
			createHoldingsEventRequest.mutate(payload, {
				onSuccess: async (response: HoldingEventRes) => {
					try {
						const description = generateHoldingEventDescription(
							tCapManage,
							response,
							holding
						);

						const updatePayload: UpdateHoldingEventDto = {
							id: response.id,
							description,
						};

						if (response.id && uploadedFile) {
							const documentInfo = await uploadDocument(response.id);

							updatePayload.documentUrl = documentInfo.documentUrl;
							updatePayload.documentName = documentInfo.documentName;
							updatePayload.documentMd5 = documentInfo.documentMd5;
						}

						await updateHoldingsEvent(updatePayload);

						createHoldingsEventForm.reset({ ...DEFAULT_FORM_VALUES });
						refreshHoldingsList();
						showToastV2({
							message: tCapManage('holdings.event.create_success'),
						});

						onClose(response.id);
					} catch {
						showToastV2({
							title: tCapManage('holdings.event.create_fail'),
							state: 'error',
						});
						createHoldingsEventRequest.reset();
					}
				},
				onError: () => {
					showToastV2({
						title: tCapManage('holdings.event.create_fail'),
						state: 'error',
					});
					createHoldingsEventRequest.reset();
				},
			});
		} catch {
			showToastV2({
				title: tCapManage('holdings.event.create_fail'),
				state: 'error',
			});
			createHoldingsEventRequest.reset();
		}
	};

	const onEditSubmit = () => async (formValues: UpdateHoldingEventDto) => {
		const eventId = holdingsEvent?.id;

		if (!eventId) {
			throw new Error('Cannot update holdings event: Missing event ID.');
		}

		// MantineDateInput already emits a timezone-safe ISO string for string-valued fields
		const formattedDate = formValues[CREATE_HOLDING_EVENT_FORM_FIELD.date] || undefined;

		const getValue = <T>(key: keyof typeof CREATE_HOLDING_EVENT_FORM_FIELD, fallback: T): T =>
			resolveEditFieldValue(formValues[CREATE_HOLDING_EVENT_FORM_FIELD[key]] as T, fallback);

		const updatedEvent: HoldingEventRes = {
			...holdingsEvent,
			date: formattedDate ?? holdingsEvent.date,
			type: getValue('type', holdingsEvent.type),
			note: getValue('note', ''),
			pledgeToPersonId: getValue('pledgeToPersonId', holdingsEvent.pledgeToPersonId),
			pledgeToString: getValue('pledgeToString', holdingsEvent.pledgeToString),
			pledgeFromHolderId: getValue('pledgeFromHolderId', holdingsEvent.pledgeFromHolderId),
			pledgeFromSecurityId: getValue(
				'pledgeFromSecurityId',
				holdingsEvent.pledgeFromSecurityId
			),
			pledgeFromString: getValue('pledgeFromString', holdingsEvent.pledgeFromString),
			assignedToPersonId: getValue('assignedToPersonId', holdingsEvent.assignedToPersonId),
			assignedToString: getValue('assignedToString', holdingsEvent.assignedToString),
			documentUrl: getValue('documentUrl', holdingsEvent.documentUrl),
			documentName: getValue('documentName', holdingsEvent.documentName),
			fileId: holdingsEvent.fileId,
		};

		if (holdingsEvent && eventId) {
			let payload: UpdateHoldingEventDto = {
				id: eventId,
				date: formattedDate,
				note: updatedEvent.note,
				description: generateHoldingEventDescription(tCapManage, updatedEvent, holding),
				pledgeToPersonId: updatedEvent.pledgeToPersonId,
				pledgeToString: updatedEvent.pledgeToString,
				pledgeFromHolderId: updatedEvent.pledgeFromHolderId,
				pledgeFromSecurityId: updatedEvent.pledgeFromSecurityId,
				pledgeFromString: updatedEvent.pledgeFromString,
				assignedToPersonId: updatedEvent.assignedToPersonId,
				assignedToString: updatedEvent.assignedToString,
				documentUrl: updatedEvent.documentUrl,
				documentName: updatedEvent.documentName,
				fileId: updatedEvent.fileId,
			};

			try {
				if (uploadedFile) {
					const documentInfo = await uploadDocument(eventId);

					payload = {
						...payload,
						documentUrl: documentInfo.documentUrl,
						documentName: documentInfo.documentName,
						documentMd5: documentInfo.documentMd5,
					};
				}

				updateHoldingsEventRequest.mutate(payload, {
					onSuccess: async () => {
						updateHoldingsEventRequest.reset();
						onClose();
						refreshHoldingsList();
						showToastV2({
							message: tCapManage('holdings.event.update_success'),
						});
					},
				});
			} catch {
				showToastV2({
					title: tCapManage('holdings.event.update_fail'),
					state: 'error',
				});
			}
		}
	};

	const removeFile = () => {
		setUploadedFile(null);
	};

	const onBulkDelete = useCallback(
		async (eventIds: string[]) => {
			return deleteHoldingsEventRequest.mutateAsync(eventIds).then(() => {
				refreshHoldingsList();
				showToastV2({
					message:
						eventIds.length > 1
							? `${eventIds.length} ${tCapManage('holdings.event.update_fail')}`
							: `${tCapManage('holdings.event.update_fail')}`,
				});
			});
		},
		[deleteHoldingsEventRequest, refreshHoldingsList]
	);

	const handleRemoveEventDocument = async (eventId: string) => {
		if (!eventId) {
			return;
		}

		try {
			await updateHoldingsEvent({
				id: eventId,
				documentUrl: '',
				documentName: '',
			});

			editHoldingsEventForm.setValue(CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl, undefined);
			editHoldingsEventForm.setValue(CREATE_HOLDING_EVENT_FORM_FIELD.documentName, undefined);
			removeFile();

			showToastV2({
				message: tCapManage('holdings.event.document_remove_success'),
			});
		} catch {
			showToastV2({
				title: tCapManage('holdings.event.document_remove_fail'),
				state: 'error',
			});
		}
	};

	const eventTypes = [
		HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES,
		HoldingEventType.PLEDGING_OF_SHARES,
		HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS,
		HoldingEventType.NOTE,
	];

	return {
		createHoldingsEventRequest,
		onSubmit,
		onEditSubmit,
		onBulkDelete,
		createHoldingsEventForm,
		updateHoldingsEventRequest,
		editHoldingsEventForm,
		selectedUnit,
		setSelectedUnit,
		selectAll,
		setSelectAll,
		uploadedFile,
		setUploadedFile,
		removeFile,
		handleRemoveEventDocument,
		entitiesSelectOptions,
		eventTypes,
	};
};
